// Sign-in state shared by all routes: one /api/me in flight at a time.

import { useEffect } from 'react';
import { fetchMe, whenSignedOut } from './api';
import { store, useStore, type Auth } from './store';

let inflight: Promise<Auth> | null = null;

export function refreshMe(): Promise<Auth> {
  if (inflight) return inflight;
  inflight = (async () => {
    const r = await fetchMe();
    let auth: Auth;
    if (r.ok) {
      store.set({ me: r.me, auth: { kind: 'in' } });
      auth = { kind: 'in' };
    } else if (r.status === 401 || r.status === 403) {
      auth = { kind: 'out', github: r.github };
      store.set({ me: null, auth });
    } else {
      auth = { kind: 'error', message: r.error ?? `HTTP ${r.status}` };
      store.set({ auth });
    }
    return auth;
  })().finally(() => {
    inflight = null;
  });
  return inflight;
}

// Signed out elsewhere (or the session ended) while a page was open: find out, and go to sign-in.
whenSignedOut(() => {
  if (store.get().auth.kind === 'in') void refreshMe();
});

/** Current auth + user; fetches /api/me on first use and retries while the hub is unreachable. */
export function useSession() {
  const auth = useStore((s) => s.auth);
  const me = useStore((s) => s.me);
  useEffect(() => {
    if (auth.kind === 'unknown') void refreshMe();
    if (auth.kind !== 'error') return;
    const t = setTimeout(() => void refreshMe(), 2000);
    return () => clearTimeout(t);
  }, [auth]);
  return { auth, me };
}
